import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, CalendarDays, ArrowRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { newsPosts } from "@/data/content";

export default function News() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("Todas");
  const [article, setArticle] = useState(null);

  const categories = ["Todas", ...new Set(newsPosts.map((n) => n.category))];

  const filtered = useMemo(
    () =>
      newsPosts.filter(
        (n) =>
          (category === "Todas" || n.category === category) &&
          (n.title + n.excerpt).toLowerCase().includes(query.toLowerCase())
      ),
    [query, category]
  );

  const [featured, ...rest] = filtered;

  return (
    <section id="noticias" data-testid="news-section" className="bg-slate-50 py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <SectionHeading
          chapter="07"
          kicker="Notícias"
          title="Acompanhe a campanha"
          description="Atualizações, agendas e bastidores do trabalho de construção de um mandato para cuidar e incluir."
        />

        <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`news-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${
                  category === c ? "bg-brand-dark text-white shadow-lg" : "bg-white text-slate-600 hover:text-brand-royal border border-slate-200"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <label className="relative block sm:w-72">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              data-testid="news-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar notícia..."
              className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-11 pr-4 text-sm text-slate-800 outline-none transition-colors focus:border-brand-royal focus:ring-2 focus:ring-brand-royal/20"
            />
          </label>
        </div>

        {filtered.length === 0 ? (
          <p data-testid="news-empty" className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
            Nenhuma notícia encontrada. Tente outro termo ou categoria.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {featured && (
              <motion.article
                layout
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                data-testid="news-featured"
                className="group flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-all hover:shadow-xl lg:row-span-3"
              >
                <div className="overflow-hidden">
                  <img src={featured.image} alt="" loading="lazy" className="aspect-video w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-wider">
                    <span className="rounded-full bg-brand-orange/15 px-3 py-1 text-brand-orange">{featured.category}</span>
                    <span className="flex items-center gap-1 text-slate-400"><CalendarDays className="h-3.5 w-3.5" />{featured.date}</span>
                  </div>
                  <h3 className="mt-4 font-display text-xl sm:text-2xl font-extrabold text-slate-900">{featured.title}</h3>
                  <p className="mt-3 flex-1 text-sm sm:text-base leading-relaxed text-slate-600">{featured.excerpt}</p>
                  <button
                    type="button"
                    data-testid="news-featured-read"
                    onClick={() => setArticle(featured)}
                    className="mt-5 inline-flex items-center gap-2 self-start text-sm font-bold text-brand-royal transition-colors hover:text-brand-orange"
                  >
                    Ler matéria completa <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </motion.article>
            )}
            {rest.map((n) => (
              <motion.article
                key={n.id}
                layout
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                data-testid={`news-card-${n.id}`}
                className="group flex gap-5 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <img src={n.image} alt="" loading="lazy" className="hidden h-full w-36 flex-shrink-0 rounded-2xl object-cover sm:block" />
                <div className="flex flex-1 flex-col py-1">
                  <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-wider">
                    <span className="rounded-full bg-brand-orange/15 px-2.5 py-0.5 text-brand-orange">{n.category}</span>
                    <span className="text-slate-400">{n.date}</span>
                  </div>
                  <h3 className="mt-2 font-display text-base font-bold text-slate-900">{n.title}</h3>
                  <p className="mt-1 line-clamp-2 flex-1 text-sm leading-relaxed text-slate-600">{n.excerpt}</p>
                  <button
                    type="button"
                    data-testid={`news-read-${n.id}`}
                    onClick={() => setArticle(n)}
                    className="mt-3 inline-flex items-center gap-1 self-start text-sm font-bold text-brand-royal transition-colors hover:text-brand-orange"
                  >
                    Ler <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {article && (
          <motion.div
            data-testid="news-article-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-brand-navy/70 p-4 backdrop-blur-sm"
            onClick={() => setArticle(null)}
          >
            <motion.article
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 16 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={article.title}
              className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white shadow-2xl"
            >
              <img src={article.image} alt="" className="aspect-[2/1] w-full rounded-t-3xl object-cover" />
              <button
                type="button"
                data-testid="news-article-close"
                aria-label="Fechar matéria"
                onClick={() => setArticle(null)}
                className="absolute right-4 top-4 rounded-full bg-white/90 p-2 text-slate-700 shadow hover:bg-white"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="p-8">
                <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-wider">
                  <span className="rounded-full bg-brand-orange/15 px-3 py-1 text-brand-orange">{article.category}</span>
                  <span className="text-slate-400">{article.date}</span>
                </div>
                <h3 className="mt-4 font-display text-2xl font-extrabold text-slate-900">{article.title}</h3>
                <p className="mt-4 text-base leading-relaxed text-slate-600">{article.body}</p>
              </div>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
